// src/modules/orders/OrderDetail.jsx
import { useState } from "react";
import { C } from "../../constants";
import { Ic } from "../../components/ui/Icons";
import { apiCall } from "../../api";
import FileUpload from "../../components/ui/FileUpload";

const STATUS_OPTIONS = ["Received", "Approved", "Paused", "Rejected", "Processed", "Delivered"];
const STATUS_COLORS = {
  "Received":  { bg: "#DBEAFE", c: "#1E40AF" },
  "Approved":  { bg: "#D1FAE5", c: "#065F46" },
  "Paused":    { bg: "#FEF3C7", c: "#92400E" },
  "Rejected":  { bg: "#FEE2E2", c: "#991B1B" },
  "Processed": { bg: "#EDE9FE", c: "#5B21B6" },
  "Delivered": { bg: "#D1FAE5", c: "#065F46" },
};

function fmtDate(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d)) return val;
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
}

function Field({ label, value, children }) {
  return (
    <div style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
      {children || <div style={{ fontSize: 14, color: value ? C.t1 : C.t3 }}>{value || "—"}</div>}
    </div>
  );
}

function Section({ title }) {
  return (
    <div style={{ padding: "16px 0 8px", borderBottom: `2px solid ${C.navy}15` }}>
      <h4 style={{ fontSize: 14, fontWeight: 700, color: C.navy, margin: 0 }}>{title}</h4>
    </div>
  );
}

function DocRow({ doc }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: C.grayBg, border: `1px solid ${C.border}`, marginBottom: 6 }}>
      <span style={{ fontSize: 16 }}>📄</span>
      <span onClick={() => window.open(doc.url, "_blank")}
        style={{ flex: 1, fontSize: 13, color: C.info, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        onMouseEnter={e => e.target.style.textDecoration = "underline"}
        onMouseLeave={e => e.target.style.textDecoration = "none"}>
        {doc.name}
      </span>
    </div>
  );
}

function EditableField({ label, value, onSave, isAdmin, type = "text", options }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(value || "");
  const [saving, setSaving]   = useState(false);

  const [saveError, setSaveError] = useState("");
  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    const result = await onSave(val);
    setSaving(false);
    if (result && !result.success) {
      setSaveError(result.error || "Save failed.");
    } else {
      setEditing(false);
    }
  };

  if (!isAdmin) {
    return <Field label={label} value={type === "date" ? fmtDate(value) : value} />;
  }

  return (
    <div style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
      {editing ? (
        <div style={{ marginTop: 4 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {options ? (
              <select value={val} onChange={e => setVal(e.target.value)}
                style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.navy}40`, background: C.grayBg, fontSize: 13, color: C.t1, outline: "none", fontFamily: "inherit" }}>
                <option value="">— Select —</option>
                {options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input type={type} value={val} onChange={e => setVal(e.target.value)} autoFocus
                onKeyDown={e => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setEditing(false); }}
                style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.navy}40`, background: C.grayBg, fontSize: 13, color: C.t1, outline: "none", fontFamily: "inherit" }} />
            )}
            <button onClick={handleSave} disabled={saving}
              style={{ background: C.navy, border: "none", borderRadius: 6, padding: "6px 12px", cursor: "pointer", color: C.white, fontSize: 13, fontFamily: "inherit" }}>
              {saving ? "..." : "Save"}
            </button>
            <button onClick={() => { setEditing(false); setVal(value || ""); setSaveError(""); }}
              style={{ background: C.grayBg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: C.t3, fontSize: 13, fontFamily: "inherit" }}>
              ✕
            </button>
          </div>
          {saveError && <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>{saveError}</div>}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14, color: value ? C.t1 : C.t3 }}>{type === "date" ? fmtDate(value) : (value || "—")}</span>
          <button onClick={() => { setEditing(true); setVal(value || ""); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, padding: 4, borderRadius: 6, display: "flex" }}
            onMouseEnter={e => { e.currentTarget.style.color = C.navy; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.t3; }}>
            <Ic.Edit />
          </button>
        </div>
      )}
    </div>
  );
}

function DocSection({ title, docs, isAdmin, orderId, docType, onUploaded }) {
  const [files, setFiles]       = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const r = await apiCall("uploadOrderFile", {
        orderId, docType,
        files: files.map(f => ({ name: f.name, mimeType: f.mimeType, base64: f.base64 })),
      });
      if (r.success) { onUploaded(docType, r.documents); setFiles([]); }
    } catch (err) { console.error("Upload failed:", err); }
    setUploading(false);
  };

  return (
    <div style={{ marginBottom: 8 }}>
      <Section title={title} />
      {docs.length > 0 ? (
        <div style={{ marginTop: 10 }}>
          {docs.map((doc, i) => <DocRow key={doc.id || i} doc={doc} />)}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: C.t3, padding: "10px 0 4px" }}>No documents attached.</p>
      )}
      {isAdmin && (
        <div style={{ marginTop: 8 }}>
          <FileUpload files={files} onChange={setFiles} />
          {files.length > 0 && (
            <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
              <button onClick={handleUpload} disabled={uploading}
                style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: C.navy, color: C.white, fontSize: 13, fontWeight: 600, cursor: uploading ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: uploading ? 0.7 : 1 }}>
                {uploading ? "Uploading..." : `Upload ${files.length} file${files.length > 1 ? "s" : ""}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OrderDetail({ order, isAdmin, onClose, onStatusChange, onUpdate, onOpenQuote }) {
  const [current, setCurrent] = useState(order);
  const [statusError, setStatusError] = useState("");

  const fmt = val => val ? "$" + Number(val).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "—";
  const sc = STATUS_COLORS[current.status] || STATUS_COLORS["Received"];

  const handleStatusChange = async (newStatus) => {
    if (newStatus === current.status) return;
    setStatusError("");
    try {
      const r = await apiCall("updateOrderStatus", { orderId: current.id, status: newStatus });
      if (r.success) {
        const updated = { ...current, status: newStatus };
        setCurrent(updated);
        onStatusChange(current.id, newStatus);
        onUpdate(updated);
      } else {
        setStatusError(r.error || "Status update failed.");
      }
    } catch (err) { setStatusError("Failed to update status."); }
  };

  const handleFieldSave = async (field, value) => {
    try {
      const r = await apiCall("updateOrderField", { orderId: current.id, field, value });
      if (r.success) {
        const updated = { ...current, [field]: value };
        setCurrent(updated);
        onUpdate(updated);
        return { success: true };
      } else {
        return { success: false, error: r.error || "Save failed." };
      }
    } catch (err) {
      console.error("Field update failed:", err);
      return { success: false, error: "Save failed. Please try again." };
    }
  };

  const handleUploaded = (docType, documents) => {
    const fieldMap = { proforma: "proformaDocs", commercial: "commercialInvoiceDocs", packing: "packingListDocs", additional: "additionalDocs" };
    const field = fieldMap[docType];
    if (field) {
      const updated = { ...current, [field]: documents };
      setCurrent(updated);
      onUpdate(updated);
    }
  };

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", justifyContent: "flex-end", zIndex: 1000 }}
        onClick={onClose}>
        <div style={{ width: "100%", maxWidth: 640, height: "100%", background: C.white, boxShadow: "-8px 0 40px rgba(0,0,0,0.12)", overflowY: "auto", animation: "slideIn 0.25s ease" }}
          onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div style={{ padding: "20px 28px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: C.white, zIndex: 1 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: sc.bg, color: sc.c }}>{current.status}</span>
              </div>
              <div style={{ fontSize: 13, color: C.t2 }}>{current.manufacturer} · {current.company}</div>
            </div>
            <button onClick={onClose}
              style={{ background: C.grayBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", cursor: "pointer", color: C.t3, display: "flex" }}
              onMouseEnter={e => { e.currentTarget.style.color = C.t1; }} onMouseLeave={e => { e.currentTarget.style.color = C.t3; }}>
              <Ic.X />
            </button>
          </div>

          {/* Status bar — admin only */}
          {isAdmin && (
            <div style={{ padding: "12px 28px", background: `${C.navy}06`, borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Status</span>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {STATUS_OPTIONS.map(opt => {
                    const active = opt === current.status;
                    const sc2 = STATUS_COLORS[opt];
                    return (
                      <button key={opt} onClick={() => handleStatusChange(opt)}
                        style={{ padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                          border: `1.5px solid ${active ? sc2.c + "60" : C.border}`,
                          background: active ? sc2.bg : C.white, color: active ? sc2.c : C.t3 }}
                        onMouseEnter={e => { if (!active) { e.currentTarget.style.background = sc2.bg; e.currentTarget.style.color = sc2.c; e.currentTarget.style.borderColor = sc2.c + "60"; }}}
                        onMouseLeave={e => { if (!active) { e.currentTarget.style.background = C.white; e.currentTarget.style.color = C.t3; e.currentTarget.style.borderColor = C.border; }}}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
              {statusError && (
                <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA" }}>
                  <p style={{ fontSize: 12, color: C.red, margin: 0 }}>{statusError}</p>
                </div>
              )}
            </div>
          )}

          {/* Meta strip */}
          <div style={{ padding: "14px 28px", background: C.grayBg, borderBottom: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Ordered Date</div><div style={{ fontSize: 13, color: C.t1, fontWeight: 500 }}>{fmtDate(current.submittedDate)}</div></div>
            <div><div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Submitted By</div><div style={{ fontSize: 13, color: C.t1 }}>{current.requestedBy || "—"}</div></div>
            <div><div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Company</div><div style={{ fontSize: 13, color: C.t1 }}>{current.company || "—"}</div></div>
          </div>

          {/* Body */}
          <div style={{ padding: "8px 28px 36px" }}>

            <Section title="Order Details" />
            <Field label="Order Amount">
              <div style={{ fontSize: 15, fontWeight: 700, color: C.t1 }}>{fmt(current.amount)}</div>
            </Field>
            <Field label="Manufacturer" value={current.manufacturer} />
            <Field label="Solution Type" value={current.solutionType} />
            <Field label="Country" value={current.country} />
            {current.projectName && <Field label="Project Name" value={current.projectName} />}

            {/* Quote Number — clickable link back to quote */}
            <Field label="Quote Reference">
              {current.quoteNumber ? (
                <button onClick={() => onOpenQuote && onOpenQuote(current.quoteId, current.quoteNumber)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: C.info, textDecoration: "underline" }}>
                  {current.quoteNumber} ↗
                </button>
              ) : <span style={{ fontSize: 13, color: C.t3 }}>—</span>}
            </Field>

            <Section title="Order Management" />
            <EditableField label="Boal PO Number" value={current.poNumber} isAdmin={isAdmin}
              onSave={v => handleFieldSave("poNumber", v)} />
            <EditableField label="Client PO Reference" value={current.clientPoRef} isAdmin={isAdmin}
              onSave={v => handleFieldSave("clientPoRef", v)} />
            <EditableField label="Payment Terms" value={current.paymentTerms} isAdmin={isAdmin}
              onSave={v => handleFieldSave("paymentTerms", v)} />
            <EditableField label="Payment Received" value={current.paymentReceived} isAdmin={isAdmin}
              options={["Yes", "No"]} onSave={v => handleFieldSave("paymentReceived", v)} />
            <EditableField label="Estimated Delivery Date" value={current.estDeliveryDate} type="date" isAdmin={isAdmin}
              onSave={v => handleFieldSave("estDeliveryDate", v)} />
            <EditableField label="Notes" value={current.notes} isAdmin={isAdmin}
              onSave={v => handleFieldSave("notes", v)} />

            {/* Admin document sections */}
            <DocSection title="Proforma Invoice" docs={current.proformaDocs || []}
              isAdmin={isAdmin} orderId={current.id} docType="proforma" onUploaded={handleUploaded} />
            <DocSection title="Commercial Invoice" docs={current.commercialInvoiceDocs || []}
              isAdmin={isAdmin} orderId={current.id} docType="commercial" onUploaded={handleUploaded} />
            <DocSection title="Packing List" docs={current.packingListDocs || []}
              isAdmin={isAdmin} orderId={current.id} docType="packing" onUploaded={handleUploaded} />
            <DocSection title="Additional Supporting Documentation" docs={current.additionalDocs || []}
              isAdmin={isAdmin} orderId={current.id} docType="additional" onUploaded={handleUploaded} />

            {/* Reference Quote — from quote file */}
            <Section title="Reference Quote" />
            {(current.quoteDocs || []).length > 0 ? (
              <div style={{ marginTop: 10 }}>
                {(current.quoteDocs || []).map((doc, i) => <DocRow key={doc.id || i} doc={doc} />)}
              </div>
            ) : (
              <p style={{ fontSize: 13, color: C.t3, padding: "10px 0 4px" }}>No quote document attached.</p>
            )}

            {/* Client Documents — PO and transfer confirmation */}
            <Section title="Client Documents" />
            <p style={{ fontSize: 12, color: C.t3, margin: "6px 0 10px" }}>Documents submitted by the client at time of order.</p>
            {(current.poFiles || []).length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.t2, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>Purchase Order</div>
                {current.poFiles.map((doc, i) => <DocRow key={doc.id || i} doc={doc} />)}
              </div>
            )}
            {(current.transferFiles || []).length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.t2, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>Bank Transfer Confirmation</div>
                {current.transferFiles.map((doc, i) => <DocRow key={doc.id || i} doc={doc} />)}
              </div>
            )}
            {(current.poFiles || []).length === 0 && (current.transferFiles || []).length === 0 && (
              <p style={{ fontSize: 13, color: C.t3, padding: "4px 0" }}>No client documents attached.</p>
            )}
          </div>
        </div>
      </div>
      <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}
